/* eslint-disable max-len */
import { checkNoChosenYears } from 'portfolio3/features/pdf/utils/checkNoChosenYears';
import { checkSectionEmptiness } from 'portfolio3/features/pdf/utils/checkSectionEmptiness';
import { sortPeriods } from 'portfolio3/features/pdf/utils/sortPeriods';
import { proforientationRecommendationSelectors } from 'portfolio3/features/proforientation/model/selectors';
import { createSelector } from 'reselect';
import { professionMetaskillsSelector } from 'selectors';
import { chosenYearsSelector } from 'selectors/drawers';
import { academicYearsTouchedSelector } from 'selectors/drawers/linkSharingDrawer';
import { getCurrentLearningYear } from 'utils';

import { professionAdditionalEducationYearsSelector } from './professionAdditionalEducationYearsSelector';
import { professionEventsYearsSelector } from './professionEventsYearsSelector';
import { professionGiaWorldSkillsYearsSelector } from './professionGiaWorldSkillsYearsSelector';
import { professionJobsYearsSelector } from './professionJobsYearsSelector';
import { professionRewardsYearsSelector } from './professionRewardsYearsSelector';
import { proforientationApplicationDodsYearsSelector } from './proforientationApplicationDodsYearsSelector';
import { proforientationApplicationExcursionsYearsSelector } from './proforientationApplicationExcursionsYearsSelector';
import { proforientationApplicationProbesYearsSelector } from './proforientationApplicationProbesYearsSelector';
import { proforientationEventDodsYearsSelector } from './proforientationEventDodsYearsSelector';
import { proforientationEventExcursionsYearsSelector } from './proforientationEventExcursionsYearsSelector';
import { proforientationEventProbesYearsSelector } from './proforientationEventProbesYearsSelector';
import { proforientationRecommendationDodsYearsSelector } from './proforientationRecommendationDodsYearsSelector';
import { proforientationRecommendationExcurionsYearsSelector } from './proforientationRecommendationExcurionsYearsSelector';
import { proforientationRecommendationProbesYearsSelector } from './proforientationRecommendationProbesYearsSelector';
import { proforientationRecommendationProforientationYearsSelector } from './proforientationRecommendationProforientationYearsSelector';

export const professionSectionAcademicYearsDataSelector = createSelector(
  [
    // Данные раздела Моя профессия
    proforientationApplicationProbesYearsSelector,
    proforientationApplicationExcursionsYearsSelector,
    proforientationApplicationDodsYearsSelector,
    proforientationRecommendationProforientationYearsSelector,
    proforientationRecommendationProbesYearsSelector,
    proforientationRecommendationExcurionsYearsSelector,
    proforientationRecommendationDodsYearsSelector,
    proforientationEventProbesYearsSelector,
    proforientationEventExcursionsYearsSelector,
    proforientationEventDodsYearsSelector,
    professionRewardsYearsSelector,
    professionJobsYearsSelector,
    professionAdditionalEducationYearsSelector,
    professionEventsYearsSelector,
    professionGiaWorldSkillsYearsSelector,
    // Выбранные года
    chosenYearsSelector,
    academicYearsTouchedSelector,
    proforientationRecommendationSelectors.proforientation,
    professionMetaskillsSelector,
  ],
  (
    proforientationApplicationProbesYears,
    proforientationApplicationExcursionsYears,
    proforientationApplicationDodsYears,
    proforientationRecommendationProforientationYears,
    proforientationRecommendationProbesYears,
    proforientationRecommendationExcurionsYears,
    proforientationRecommendationDodsYears,
    proforientationEventProbesYears,
    proforientationEventExcursionsYears,
    proforientationEventDodsYears,
    professionRewardsYears,
    professionJobsYears,
    professionAdditionalEducationYears,
    professionEventsYears,
    professionGiaWorldSkillsYears,
    chosenYears,
    academicYearsTouched,
    proforientationData,
    metaskills,
  ) => {
    const currentLearningYear = getCurrentLearningYear();

    const emptyDataMap = {
      professionRewards: checkNoChosenYears(professionRewardsYears, chosenYears, academicYearsTouched),
      job: checkNoChosenYears(professionJobsYears, chosenYears, academicYearsTouched),
      metaskills: metaskills.content.length === 0,
      professionEducation: checkNoChosenYears(professionAdditionalEducationYears, chosenYears, academicYearsTouched),
      professionEvents: checkNoChosenYears(professionEventsYears, chosenYears, academicYearsTouched),
      professionWorldskills: checkNoChosenYears(professionGiaWorldSkillsYears, chosenYears, academicYearsTouched),
      professionCareerGuidance: checkNoChosenYears(professionJobsYears, chosenYears, academicYearsTouched),
      professionRecommendations:
        checkNoChosenYears(proforientationRecommendationProbesYears, chosenYears, academicYearsTouched) &&
        checkNoChosenYears(proforientationRecommendationExcurionsYears, chosenYears, academicYearsTouched) &&
        checkNoChosenYears(proforientationRecommendationDodsYears, chosenYears, academicYearsTouched) &&
        checkNoChosenYears(proforientationRecommendationProforientationYears, chosenYears, academicYearsTouched) &&
        // Рекомендации должны быть доступны, если выбран текущий год и есть данные по рекомендованым отраслям
        !((chosenYears.includes(currentLearningYear) || !academicYearsTouched) && proforientationData.length > 0),
      professionProfEvents:
        checkNoChosenYears(proforientationEventProbesYears, chosenYears, academicYearsTouched) &&
        checkNoChosenYears(proforientationEventExcursionsYears, chosenYears, academicYearsTouched) &&
        checkNoChosenYears(proforientationEventDodsYears, chosenYears, academicYearsTouched),
      professionApplications:
        checkNoChosenYears(proforientationApplicationProbesYears, chosenYears, academicYearsTouched) &&
        checkNoChosenYears(proforientationApplicationExcursionsYears, chosenYears, academicYearsTouched) &&
        checkNoChosenYears(proforientationApplicationDodsYears, chosenYears, academicYearsTouched),
    };

    const periods: Set<string> = new Set([
      ...proforientationApplicationProbesYears,
      ...proforientationApplicationExcursionsYears,
      ...proforientationApplicationDodsYears,
      ...proforientationRecommendationProforientationYears,
      ...proforientationRecommendationProbesYears,
      ...proforientationRecommendationExcurionsYears,
      ...proforientationRecommendationDodsYears,
      ...proforientationEventProbesYears,
      ...proforientationEventExcursionsYears,
      ...proforientationEventDodsYears,
      ...professionRewardsYears,
      ...professionJobsYears,
      ...professionAdditionalEducationYears,
      ...professionEventsYears,
      ...professionGiaWorldSkillsYears,
    ]);

    return {
      academicYears: sortPeriods(periods),
      emptyDataMap: {
        ...emptyDataMap,
        profession: checkSectionEmptiness(emptyDataMap),
      },
    };
  },
);
